import * as THREE from "three";
import { flat, Tone } from "./materials";
import { Slot, slotNormal } from "./palette";

export type ItemId = "glasses" | "clock" | "torch";

export interface Item {
  id: ItemId;
  name: string;
  /** the world model, where there is one; the torch is worn by the character instead */
  build?(): THREE.Group;
}

const tag = (mesh: THREE.Mesh, slot: number, variant: number): THREE.Mesh => {
  mesh.userData.slot = slotNormal(slot);
  mesh.userData.variant = variant;
  return mesh;
};

function buildGlasses(): THREE.Group {
  const group = new THREE.Group();
  const frame = flat(Tone.ink);
  const glass = flat(Tone.bone);

  for (const side of [-1, 1]) {
    // a torus already lies in XY with its axis on Z, so the lens faces forward
    const rim = new THREE.Mesh(new THREE.TorusGeometry(0.125, 0.021, 6, 18), frame);
    rim.position.set(side * 0.15, 0, 0);
    group.add(tag(rim, Slot.metal, 0.42));

    const lens = new THREE.Mesh(new THREE.CylinderGeometry(0.108, 0.108, 0.012, 18), glass);
    lens.position.set(side * 0.15, 0, 0);
    lens.rotation.x = Math.PI / 2;
    group.add(tag(lens, Slot.metal, 0.86));
  }

  const bridge = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.022, 0.022), frame);
  bridge.position.set(0, 0.035, 0);
  group.add(tag(bridge, Slot.metal, 0.42));

  for (const side of [-1, 1]) {
    const arm = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.018, 0.19), frame);
    arm.position.set(side * 0.29, 0.03, -0.08);
    group.add(tag(arm, Slot.metal, 0.42));
  }

  // The icon camera sits at 45 degrees of azimuth, so the frame is turned to
  // match it: the lenses then read as two circles side by side rather than a
  // pair sliding off on the diagonal.
  group.rotation.set(-0.1, 0.785, 0.05);
  group.position.y = -0.02;
  return group;
}

function buildClock(): THREE.Group {
  const group = new THREE.Group();
  const brass = flat(Tone.ash);
  const dark = flat(Tone.ink);

  const caseBody = new THREE.Mesh(new THREE.CylinderGeometry(0.155, 0.155, 0.045, 22), brass);
  caseBody.rotation.x = Math.PI / 2;
  group.add(tag(caseBody, Slot.metal, 0.78));

  const face = new THREE.Mesh(new THREE.CylinderGeometry(0.135, 0.135, 0.014, 22), flat(Tone.paper));
  face.rotation.x = Math.PI / 2;
  face.position.z = 0.026;
  group.add(tag(face, Slot.plaster, 0.9));

  const crown = new THREE.Mesh(new THREE.CylinderGeometry(0.026, 0.03, 0.05, 8), brass);
  crown.position.set(0, 0.175, 0);
  group.add(tag(crown, Slot.metal, 0.7));

  const loop = new THREE.Mesh(new THREE.TorusGeometry(0.038, 0.011, 6, 14), brass);
  loop.position.set(0, 0.225, 0);
  group.add(tag(loop, Slot.metal, 0.7));

  const minute = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.1, 0.01), dark);
  minute.position.set(0, 0.048, 0.036);
  group.add(tag(minute, Slot.metal, 0.3));

  const hour = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.013, 0.01), dark);
  hour.position.set(0.024, -0.005, 0.036);
  group.add(tag(hour, Slot.metal, 0.3));

  // turned most of the way to the icon camera: the face stays round enough to
  // read, with just enough angle to keep a little dimension
  group.rotation.set(-0.2, 0.46, 0.03);
  group.position.y = 0.04;
  return group;
}

export const ITEMS: Record<ItemId, Item> = {
  torch: { id: "torch", name: "torch" },
  glasses: { id: "glasses", name: "glasses", build: buildGlasses },
  clock: { id: "clock", name: "clock", build: buildClock },
};
